import { useCallback } from 'react';

import { useLang } from 'locale';

import { useToast } from 'providers/Toast';

import { ApiError, extractApiError } from 'services/api';

import { MutationErrorParser, MutationMessage, MutationMessageConfig } from './definition';

export function useMutationMessages() {
  const { addToast } = useToast();
  const { t } = useLang();

  const parseMessage = useCallback(
    (message: MutationMessage) => {
      if (typeof message !== 'string') return message;

      const translated = t(message);

      const hasTranslation = translated !== message;

      return { title: hasTranslation ? translated : message };
    },
    [t],
  );

  const showError = useCallback(
    (
      error: ApiError,
      {
        customParser,
        config,
      }: { customParser?: MutationErrorParser; config?: MutationMessageConfig } = {},
    ) => {
      if (config?.suppressError) return;

      if (config?.error) return addToast({ ...parseMessage(config?.error), type: 'error' });

      const customMessage = customParser?.(error || {});

      if (customMessage) return addToast({ ...parseMessage(customMessage), type: 'error' });

      addToast({
        title: error.message.startsWith('Validation:') ? t('validation-error') : error.message,
        type: 'error',
      });
    },
    [addToast, parseMessage, t],
  );

  const showSuccess = useCallback(
    (config?: MutationMessageConfig) => {
      if (config?.suppressSuccess) return;

      const message = config?.success;

      addToast({
        ...(message ? parseMessage(message) : { title: t('success') }),
        type: 'success',
      });
    },
    [addToast, parseMessage, t],
  );

  return {
    showSuccess,
    showError,
    extractApiError,
  };
}
